import { useTranslation } from "react-i18next";
import { IVariant } from "redux/reducers/models/product";
import { IModifierGroup } from "api/rpc/facilityAdmin/modifierGroups";
import Sheet from "components/sheet/Sheet";
import React, { useMemo } from "react";
import "./selectModifierSheet.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import Spin from "components/spin/spin";
import { LocaleCurrency } from "helpers/Locale";
import { ButtonNew as Button } from "components/buttonNew";
import classNames from "classnames";

interface ISelectModifierSheetProps {
  open: boolean;
  onCancel: () => void;
  onOk: () => void;
  onClear?: () => void;
  allModifierGroups: IModifierGroup[];
  selectedModifierGroupVariants: { [key: string]: Record<string, any>[] };
  addModifierVariant: (modifierGroup: IModifierGroup, variant: IVariant, selected: boolean) => void;
  changeModifierQuantity?: (modifierGroup: IModifierGroup, variant: IVariant, quantity: number) => void;
  allowModifierQuantity?: boolean;
  okDisabled: boolean;
  currency: string;
  productTitle?: string;
  isEditing?: boolean;
}

export default function SelectModifierSheet(props: ISelectModifierSheetProps) {
  const { t } = useTranslation();
  const {
    open,
    onCancel,
    onOk,
    onClear,
    allModifierGroups,
    selectedModifierGroupVariants,
    addModifierVariant,
    changeModifierQuantity,
    allowModifierQuantity,
    okDisabled,
    currency,
    isEditing,
  } = props;

  const selectedCount = useMemo(() => {
    if (!selectedModifierGroupVariants) {
      return 0;
    }
    return Object.values(selectedModifierGroupVariants).reduce((sum, variants) => sum + (variants?.length ?? 0), 0);
  }, [selectedModifierGroupVariants]);

  const hasSelections = selectedCount > 0;

  function getSortedVariants(modifierGroup: IModifierGroup) {
    return [...(modifierGroup.variants ?? [])].sort(
      (a: any, b: any) => (a.modifier_group?.position ?? 0) - (b.modifier_group?.position ?? 0),
    ) as IVariant[];
  }

  function getGroupSelectionLabel(modifierGroup: IModifierGroup) {
    const groupKey = String(modifierGroup.id);
    const groupSelectedCount = selectedModifierGroupVariants?.[groupKey]?.length ?? 0;
    if (modifierGroup.multi_select) {
      return `${groupSelectedCount} ${t("selected")}`;
    }
    return `${groupSelectedCount}/1 ${t("selected")}`;
  }

  return (
    <Sheet
      size="small"
      title={t("Select modifiers")}
      supportText={t("Choose modifiers to add to the product.")}
      open={open}
      onCancel={onCancel}
      footer={null}
      closable
      backDropCancel={false}
      stacked
    >
      
      {!allModifierGroups || allModifierGroups?.length === 0 ? (
        <div className="select-modifier-sheet-loading">
          <div className="select-modifier-sheet-loading-spin-container">
            <Spin />
          </div>
          <div>{t("Loading modifier groups...")}</div>
        </div>
      ) : (
        <div className="select-modifier-sheet-groups">
          {allModifierGroups.map(modifierGroup => {
            const isRequired = modifierGroup.required || !!modifierGroup.modifier?.required;

            return (
              <div key={modifierGroup.id} className="select-modifier-sheet-group">
                <div className="select-modifier-sheet-group-header">
                  <div className="select-modifier-sheet-group-heading">
                    <span>{modifierGroup.title}</span>
                    {isRequired ? <span className="select-modifier-sheet-group-required">{t("Required")}</span> : null}
                  </div>
                  <div className="select-modifier-sheet-group-selected">
                    {getGroupSelectionLabel(modifierGroup)}
                  </div>
                </div>

                <div className="select-modifier-sheet-variants">
                  {getSortedVariants(modifierGroup).map((variant: IVariant) => {
                    const groupKey = String(modifierGroup.id);
                    const selectedVariant = selectedModifierGroupVariants?.[groupKey]?.find(
                      modGroup => modGroup.id === variant.id,
                    );
                    const selected = !!selectedVariant;
                    const quantity = Number(selectedVariant?.quantity) || 1;
                    const preferredTitle =
                      variant.product?.meta?.preferred_title || variant.product?.preferred_title || null;
                    const hasMultipleVariants = (variant.product?.variant_count ?? 0) > 1;
                    const primaryTitle = hasMultipleVariants
                      ? variant.title
                      : preferredTitle || variant.product?.title || variant.title;

                    return (
                      <div key={variant.id} className="select-modifier-sheet-variant">
                        <div
                          className="variant-content"
                          style={{ backgroundColor: variant.product?.meta?.button_color }}
                          onClick={!selected ? () => addModifierVariant(modifierGroup, variant, false) : undefined}
                        >
                          <div className="variant-content-titles">
                            <span className="variant-content-title">{primaryTitle}</span>
                            {hasMultipleVariants ? (
                              <span className="variant-content-product-title">{preferredTitle || variant.product?.title}</span>
                            ) : null}
                            <p className="variant-content-price">
                              <LocaleCurrency currency={currency} amount={Number(variant?.price ?? 0).toFixed(2)} />
                            </p>
                          </div>

                          {selected ? (
                            <div className="variant-content-quantity-selection">
                              <FontAwesomeIcon
                                className="variant-content-quantity-selection-icon"
                                icon={faMinus}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (allowModifierQuantity && quantity > 1 && changeModifierQuantity) {
                                    changeModifierQuantity(modifierGroup, variant, quantity - 1);
                                    return;
                                  }
                                  addModifierVariant(modifierGroup, variant, true);
                                }}
                              />
                              <p>{quantity}</p>
                              <FontAwesomeIcon
                                className={classNames(
                                  "variant-content-quantity-selection-icon",
                                  {
                                    "variant-content-quantity-selection-icon-disabled": !allowModifierQuantity,
                                  },
                                )}
                                icon={faPlus}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (allowModifierQuantity && changeModifierQuantity) {
                                    changeModifierQuantity(modifierGroup, variant, quantity + 1);
                                  }
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="select-modifier-sheet-footer">
        <Button type="text" onClick={onClear} disabled={!onClear || !hasSelections}>
          {t("Clear")}
        </Button>
        <div className="select-modifier-sheet-footer-actions">
          <Button type="secondary" onClick={onCancel}>
            {t("Go back")}
          </Button>
          <Button type="primary" onClick={onOk} disabled={okDisabled}>
            {isEditing ? t("Update changes") : t("Continue")}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
